import type { GroupKey, SectionKey } from './groups';

/**
 * One way to get to a field, used by the checklist ("Doplnit ›"), the search results, the in-page
 * links and old `?tab=` deep links: switch group, wait for the field to exist, open whatever hides
 * it, scroll it clear of the sticky tab row, focus it and flash it.
 *
 * <p>The waiting is real: after the group switches, React has not rendered the new group yet, and
 * the guide only mounts its fields once its config has loaded.
 */

export interface JumpRequest {
  group: GroupKey;
  section?: SectionKey;
  /** Field to land on. */
  targetId?: string;
  /** Used when `targetId` never appears (e.g. the door code while the access type is "in person"). */
  fallbackId?: string;
}

export interface JumpOptions {
  /** Switches the visible group (and writes the URL). Called only when the group has to change. */
  showGroup: (group: GroupKey) => void;
  currentGroup: () => string;
  /** Give up waiting for the exact field after this long and use the section instead. */
  timeoutMs?: number;
  highlightMs?: number;
}

export const sectionDomId = (section: SectionKey) => `sec-${section}`;

const FOCUSABLE = 'input, select, textarea, button, a[href], [tabindex]';

let highlightTimer: ReturnType<typeof setTimeout> | null = null;
let highlighted: Element | null = null;

function clearHighlight() {
  if (highlightTimer) clearTimeout(highlightTimer);
  highlightTimer = null;
  highlighted?.classList.remove('ae-highlight');
  highlighted = null;
}

/** Space the sticky chrome takes at the top of the viewport: the app's nav (desktop) + the tab row. */
function stickyOffset(): number {
  const bar = document.querySelector<HTMLElement>('[data-ae-sticky]');
  if (!bar) return 0;
  const top = parseFloat(window.getComputedStyle(bar).top);
  return (Number.isFinite(top) ? top : 0) + bar.offsetHeight;
}

function openAncestors(el: Element) {
  for (let n = el.parentElement; n; n = n.parentElement) {
    if (n instanceof HTMLDetailsElement && !n.open) n.open = true;
  }
  if (el instanceof HTMLDetailsElement && !el.open) el.open = true;
  // A collapsed section card (`<section class="ae-cx"><details>…`): jumping to the section itself
  // has to open it, otherwise the host lands on a closed one-line header.
  if (el.classList.contains('ae-cx')) {
    const own = el.querySelector(':scope > details');
    if (own instanceof HTMLDetailsElement && !own.open) own.open = true;
  }
}

function reveal(el: HTMLElement, section: HTMLElement | null, highlightMs: number) {
  openAncestors(el);
  // Scroll the whole field (label included), not just the control, clear of the sticky tab row.
  const box = (el.closest('.form-field, .ae-field') as HTMLElement | null) || el;
  box.style.scrollMarginTop = `${stickyOffset() + 16}px`;
  if (typeof box.scrollIntoView === 'function') {
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.scrollIntoView({ block: 'start', behavior: reduce ? 'auto' : 'smooth' });
  }

  if (el.matches(FOCUSABLE) && !el.hasAttribute('disabled')) {
    el.focus({ preventScroll: true });
  } else if (section) {
    section.focus({ preventScroll: true });
  }

  clearHighlight();
  box.classList.add('ae-highlight');
  highlighted = box;
  highlightTimer = setTimeout(clearHighlight, highlightMs);
}

/**
 * Returns a cancel function. Safe to call repeatedly: a newer jump cancels an older one that is
 * still waiting.
 */
let cancelPrevious: (() => void) | null = null;

export function jumpTo(request: JumpRequest, options: JumpOptions): () => void {
  cancelPrevious?.();
  const { timeoutMs = 2000, highlightMs = 1500 } = options;

  if (options.currentGroup() !== request.group) options.showGroup(request.group);

  const sectionId = request.section ? sectionDomId(request.section) : null;
  const started = Date.now();
  let sectionSeenAt: number | null = null;
  let stopped = false;
  let raf = 0;

  const stop = () => {
    stopped = true;
    if (raf) cancelAnimationFrame(raf);
  };
  cancelPrevious = stop;

  const step = () => {
    if (stopped) return;
    const section = sectionId ? document.getElementById(sectionId) : null;
    const exact = request.targetId ? document.getElementById(request.targetId) : null;

    if (exact) {
      reveal(exact, section, highlightMs);
      return;
    }

    if (section) {
      if (sectionSeenAt === null) sectionSeenAt = Date.now();
      const busy = section.getAttribute('aria-busy') === 'true';
      const settled = !busy && Date.now() - sectionSeenAt > 120;
      const expired = Date.now() - started > timeoutMs;
      if (settled || expired) {
        const fallback = request.fallbackId ? document.getElementById(request.fallbackId) : null;
        if (fallback) reveal(fallback, section, highlightMs);
        else reveal(section, section, highlightMs);
        return;
      }
    } else if (Date.now() - started > timeoutMs) {
      return;
    }

    raf = requestAnimationFrame(step);
  };

  raf = requestAnimationFrame(step);
  return stop;
}
